export interface BridgeTemplate {
  id: string
  eyebrow: string
  headlineLead: string
  headlineHighlight: string
  headlineTrail: string
  subhead: string
  stepLabel: string
  cardTitle: string
  cardSubtitle: string
  ctaLabel: string
  // Optional: form variant. 'email' (default) shows the email capture form.
  // 'cta' shows a simple call-to-action card with no email field.
  variant?: 'email' | 'cta' | 'selector'
  // Optional copy used by the 'cta' variant
  cardHeadline?: string
  cardSubcopy?: string
  cardFootnote?: string
  testimonials: {
    initial: string
    quote: string
    name: string
    school: string
  }[]
  stats: {
    value: string
    label: string
  }[]
  financialAid?: {
    grantAmount: string
    grantCaption: string
    earningsStat: string
    earningsDisclaimer: string
    weCanHeadline: string
    weCanBody: string
    exploreHeadline: string
    exploreBody: string
  }
  footerDisclosure?: string
}

export const bridgeTemplates: Record<string, BridgeTemplate> = {
  v1: {
    id: 'v1',
    eyebrow: 'FINAL STEP — CONFIRM YOUR MATCH',
    headlineLead: 'Your',
    headlineHighlight: 'Free Program Matches',
    headlineTrail: 'Are Ready to View',
    subhead:
      'Thousands of accredited online programs are waiting. Confirm your email to unlock your personalized results — it takes 10 seconds.',
    stepLabel: 'STEP 2 OF 2 · ALMOST DONE',
    cardTitle: 'Confirm Your Email Address',
    cardSubtitle:
      "We'll send your matched programs here. No spam — just your results.",
    ctaLabel: 'View My Program Matches',
    testimonials: [
      {
        initial: 'M',
        quote:
          'I found an accredited online MBA in under 5 minutes. The matching process was incredibly easy.',
        name: 'Maria T.',
        school: 'Enrolled at SNHU',
      },
      {
        initial: 'D',
        quote:
          'Was skeptical at first, but they matched me with exactly the criminal justice program I needed.',
        name: 'Derek L.',
        school: 'Enrolled at Liberty University Online',
      },
    ],
    stats: [
      {
        value: '200+',
        label: 'Accredited Schools',
      },
      {
        value: '1,400+',
        label: 'Online Programs',
      },
      {
        value: '100%',
        label: 'Free Matching',
      },
    ],
  },
  v2: {
    id: 'v2',
    eyebrow: 'NEXT SESSION STARTS SOON',
    headlineLead: 'Earn an',
    headlineHighlight: 'Online Degree',
    headlineTrail: 'On Your Schedule',
    subhead:
      'Flexible, accredited online programs designed for working adults. Explore your options and take the first step toward your future today.',
    stepLabel: 'GET STARTED',
    cardTitle: 'Earn an Online Degree',
    cardSubtitle: 'Next session starts soon.',
    ctaLabel: 'Get Started',
    variant: 'cta',
    cardHeadline: 'Earn an Online Degree',
    cardSubcopy: 'Next session starts soon.',
    cardFootnote:
      'Want to learn more? Our admissions team is here to support you and get your questions answered.',
    testimonials: [
      {
        initial: 'J',
        quote:
          'The online format gave me the flexibility to keep working full-time while earning my degree. Best decision I ever made.',
        name: 'Jasmine R.',
        school: "Bachelor's in Business Administration",
      },
      {
        initial: 'A',
        quote:
          'Supportive instructors and a curriculum that actually applies to my career. I finished in under three years.',
        name: 'Andre P.',
        school: "Master's in IT Management",
      },
    ],
    stats: [
      { value: '200+', label: 'Accredited Schools' },
      { value: '1,400+', label: 'Online Programs' },
      { value: '100%', label: 'Free Matching' },
    ],
  },
}

// v3, v4, v5 mirror v2's CTA content. Created as separate IDs so they can
// diverge later (e.g. for A/B test variants) without code changes.
// v5's click behavior routes the user to the /colleges page.
bridgeTemplates.v3 = { ...bridgeTemplates.v2, id: 'v3' }
bridgeTemplates.v4 = { ...bridgeTemplates.v2, id: 'v4' }
bridgeTemplates.v5 = { ...bridgeTemplates.v2, id: 'v5' }
// v7 mirrors v6 (selector variant), but redirects externally to media-matchers
bridgeTemplates.v7 = null as unknown as BridgeTemplate // populated below after v6

// v6: dropdown selector variant — user picks degree level, area, and program,
// then is sent to the listings page with those filters prepopped via URL params.
bridgeTemplates.v6 = {
  id: 'v6',
  eyebrow: 'FIND YOUR PROGRAM',
  headlineLead: 'Get Started in',
  headlineHighlight: '60 Seconds',
  headlineTrail: '',
  subhead:
    "Tell us a little about your educational goals and we'll match you with accredited online programs that fit.",
  stepLabel: 'STEP 1 OF 1',
  cardTitle: 'Get Started in 60 Seconds',
  cardSubtitle: 'Tell us about your educational goals',
  ctaLabel: 'Continue to Next Step',
  variant: 'selector',
  testimonials: [
    {
      initial: 'M',
      quote:
        'I found an accredited online MBA in under 5 minutes. The matching process was incredibly easy.',
      name: 'Maria T.',
      school: 'Enrolled at SNHU',
    },
    {
      initial: 'D',
      quote:
        'Was skeptical at first, but they matched me with exactly the criminal justice program I needed.',
      name: 'Derek L.',
      school: 'Enrolled at Liberty University Online',
    },
  ],
  stats: [
    { value: '200+', label: 'Accredited Schools' },
    { value: '1,400+', label: 'Online Programs' },
    { value: '100%', label: 'Free Matching' },
  ],
}

// v7: same selector UI as v6, different submission target (external API redirect)
bridgeTemplates.v7 = { ...bridgeTemplates.v6, id: 'v7' }

// v8: same selector UI as v6/v7, but on submit fires the Clicks Search
// API in-page and renders results as a one-at-a-time carousel (same UX
// as the post-form step 8 in the request-information flow).
bridgeTemplates.v8 = { ...bridgeTemplates.v6, id: 'v8' }

// healthcare-v1: vertical-specific CTA template. Uses v2's single-button
// CTA style (no email field, no selectors) but on click swaps the page
// for the v8-style Clicks API carousel. mProgram is hardcoded to 500
// (Healthcare) inside FindDegreesBridgePage so users land directly on
// matching healthcare programs.
bridgeTemplates['healthcare-v1'] = {
  id: 'healthcare-v1',
  eyebrow: 'HEALTHCARE PROGRAMS · ENROLLING NOW',
  headlineLead: 'Launch Your',
  headlineHighlight: 'Healthcare Career',
  headlineTrail: 'Online',
  subhead:
    'Accredited online healthcare degrees and certificates — from nursing and medical assisting to health information management. Flexible schedules built for working adults.',
  stepLabel: 'GET STARTED',
  cardTitle: 'Find Your Healthcare Program',
  cardSubtitle: 'Match with accredited online schools in seconds.',
  ctaLabel: 'See My Healthcare Matches',
  variant: 'cta',
  cardHeadline: 'Find Your Healthcare Program',
  cardSubcopy: 'Match with accredited online schools in seconds.',
  cardFootnote:
    'Browse top-rated healthcare programs from accredited universities. No commitment, completely free.',
  testimonials: [
    {
      initial: 'L',
      quote:
        'I went from working retail to becoming a Registered Nurse — the online flexibility made it possible while raising my kids.',
      name: 'Lauren K.',
      school: 'BSN, Accredited Online Program',
    },
    {
      initial: 'D',
      quote:
        'The healthcare administration program gave me real skills I could apply immediately. Got promoted within a year of graduating.',
      name: 'Devon M.',
      school: 'MHA, Healthcare Administration',
    },
  ],
  stats: [
    { value: '120+', label: 'Healthcare Programs' },
    { value: '50+', label: 'Accredited Schools' },
    { value: '100%', label: 'Free Matching' },
  ],
}

bridgeTemplates['financial-aid-v1'] = {
  id: 'financial-aid-v1',
  eyebrow: 'FINANCIAL AID · GRANT FUNDING AVAILABLE',
  headlineLead: 'Now Is the Perfect Time to',
  headlineHighlight: 'Go Back to School',
  headlineTrail: '',
  subhead:
    'Grant funding may be available to those who qualify. In as little as 2 minutes, get matched to schools that may fit your needs and goals.',
  stepLabel: 'STEP 1 OF 1',
  cardTitle: 'Search Featured Schools',
  cardSubtitle: 'Tell us what you want to study',
  ctaLabel: 'Next',
  variant: 'selector',
  financialAid: {
    grantAmount: '$7,395',
    grantCaption:
      'Grant programs provide up to $7,395 *per year to those who qualify',
    earningsStat:
      'On an annual basis, median earnings for bachelor’s degree holders are about $90,000 — 81% higher than the $50,000 median earnings of those whose highest education is a high school diploma.',
    earningsDisclaimer:
      '(Based on national data, not school-specific information. Conditions in your area may vary.)**',
    weCanHeadline: 'We Can Help',
    weCanBody:
      'In as little as 2 minutes, you can get matched to schools that may fit your needs and goals.',
    exploreHeadline: 'Explore Financial Aid Options',
    exploreBody:
      'Speak with a representative to find out what financial aid options you may qualify for.',
  },
  footerDisclosure:
    'Disclosure: FindOnlineDegrees is an educational consumer information source connecting students with programs and does not provide direct educational services. FindOnlineDegrees is not responsible for education program content or for third-party referral sources. Educational program options vary. Programs may not be accelerated, available in all locations, or available both online and at on-campus locations. Financial aid may be available to those who qualify including grants, scholarships, and other programs. Financial aid and grants are provided directly to educational programs, not to students, unless otherwise noted. To receive financial aid and grants, students must submit applications and be accepted to educational programs for which aid will be used. Employment is not implied nor guaranteed, and each program length and curriculum will vary based on the institution you select. FindOnlineDegrees offers free information, ratings, and recommendations of colleges and universities and receives referral, promotional, and/or advertising revenue from some of the schools we review. *https://studentaid.gov/understand-aid/types/grants/pell **https://www.bls.gov/charts/usual-weekly-earnings/usual-weekly-earnings-by-quartiles-and-selected-deciles-by-education.htm',
  testimonials: [
    {
      initial: 'M',
      quote:
        'I found an accredited online MBA in under 5 minutes. The matching process was incredibly easy.',
      name: 'Maria T.',
      school: 'Enrolled at SNHU',
    },
    {
      initial: 'D',
      quote:
        'Was skeptical at first, but they matched me with exactly the criminal justice program I needed.',
      name: 'Derek L.',
      school: 'Enrolled at Liberty University Online',
    },
  ],
  stats: [
    { value: '200+', label: 'Accredited Schools' },
    { value: '1,400+', label: 'Online Programs' },
    { value: '100%', label: 'Free Matching' },
  ],
}

export function getBridgeTemplate(id: string | undefined): BridgeTemplate {
  if (!id) return bridgeTemplates.v1
  return bridgeTemplates[id] || bridgeTemplates.v1
}
